import api from "./api"
import { AxiosError } from "axios"
import { ManagementCompany } from "../Interfaces";
import { showErroPage } from "../utils/helper";

class ManagementCompanyService {
  private baseUrl = "/managementCompany";

  public create = async (managementCompanyInfo: any, imageFile: File | null = null) => {
    if (!managementCompanyInfo) throw new Error("No management company info provided")
    try {
      const response = await api.post(
        `${this.baseUrl}`,
        managementCompanyInfo,
      );
      if (imageFile) await this.uploadLogo(imageFile, response.data.id, response.data.name)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public uploadLogo = async (imageFile: File, managementCompanyId: number, managementCompanyName: string) => {
    if (!imageFile) return;
    try {
      const { data: signedUrl } = await api.get(
        `${this.baseUrl}/update/logo/${managementCompanyId}/${managementCompanyName}`,
      )
      if (imageFile.type !== "image/jpeg" && imageFile.type !== "image/png" && imageFile.type !== "image/jpg") throw new Error("Invalid file type")
      const response = await api.put(
        signedUrl,
        imageFile,
        {
          headers: {
            "Content-Type": imageFile.type,
            "Authorization": undefined,
          }
        }
      );
      return response.data;
    } catch (error) {
      return error as Error;
    }
  };

  public getViewableLogo = async (managementCompanyId: number, managementCompanyName: string) => {
    try {
      const { data: signedUrl } = await api.get(
        `${this.baseUrl}/logo/${managementCompanyId}/${managementCompanyName}`,
      )
      return signedUrl;
    } catch (error) {
      return error as Error;
    }
  }

  public getOne = async (id: number) => {
    if (!id) throw new Error("No id provided")
    try {
      const response = await api.get(
        `${this.baseUrl}/${id}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public getByLocation = async (locationId: number): Promise<ManagementCompany | undefined> => {
    if (!locationId) throw new Error("No locationId provided")
    try {
      const response = await api.get(
        `${this.baseUrl}/by-location/${locationId}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response)
        const statusText = error.response?.statusText
        throw new Error(statusText)
      } else {
        console.error(error)
        showErroPage()
        return undefined
      }
    }
  }

  public getByUser = async (userId: number): Promise<ManagementCompany | undefined> => {
    if (!userId) throw new Error("No userId provided")
    try {
      const response = await api.get(
        `${this.baseUrl}/by-user/${userId}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response)
        const statusText = error.response?.statusText
        throw new Error(statusText)
      } else {
        console.error(error)
        showErroPage()
        return undefined
      }
    }
  }

  public getAll = async (userId: number) => {
    try {
      const response = await api.get(
        `${this.baseUrl}/by-user/${userId}`
      );
      return response.data.companies
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public getSome = async (userId: number, limit: number, page: number, searchTerm?: string) => {
    try {
      const offset = (page - 1) * limit;
      const response = await api.get(
        `${this.baseUrl}/some?limit=${limit}&offset=${offset}&searchTerm=${searchTerm || ""}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public update = async (managementCompanyInfo: any, imageFile: File | null = null) => {
    if (!managementCompanyInfo) throw new Error("No management company info provided")
    try {
      const response = await api.put(
        `${this.baseUrl}`,
        managementCompanyInfo
      );
      if (imageFile) await this.uploadLogo(imageFile, response.data.id, response.data.name)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public delete = async (id: number) => {
    if (!id) throw new Error("No id provided")
    try {
      const response = await api.delete(
        `${this.baseUrl}/${id}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }
}

export const managementCompanyService = new ManagementCompanyService()

